import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { CoreRequestFilterModel, CoreRequestParams, CoreResponse, EntityService } from '@scrollmax/core-nextgen';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { CoreService } from './core.service';
import { environment } from '../../../environments/environment';
import { SanitizerService } from './sanitizer.service';
import { NoticeService } from './notice.service';
import { EDownloadableFile, TDownloadableFile } from '../models/printportal';

import { Nev, Partner, PartnerCim } from '../entity/scroll';
import { SpTaska } from '../entity/printportal';

@Injectable({
  providedIn: 'root'
})
export class PrintportalService {

    // app-wide az utoljára kiválasztott táskát fogjuk benne tárolni
    selectedTaskaState = signal<SpTaska>(null);
    // app-wide az utoljára kiválasztott táska id-t fogjuk benne tárolni (hasznos ha vissza akarunk lépni valamilyen oldalra pl.)
    selectedTaska = signal<number>(null);

    stateNev: Subject<void> = new Subject();

    constructor(
      private http: HttpClient, 
      private entityService: EntityService,
      private noticeService: NoticeService,
      private coreService: CoreService
    ) { 

    }

    /**
     * Partner entitások lekérdezése a kiszolgálótól
     * 
     * @returns 
     */
    getPartners(): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/partner/${this.coreService.getNyomdaId()}`)
        .pipe(map((r) => this.entityService.parse(r).getResponse()));
    }

    /**
     * Csak a partner entitást változtatjuk meg! Gyakorlatilag a partner bizonyos adatainak 
     * visszamentése a törzsbe.
     * 
     * @param partner 
     * @param isOrder 
     */
    changePartner(partner: Partner, isOrder: boolean = false): Observable<CoreResponse> {

      let params = new HttpParams();
      params = params.append('tel', partner.getTel());
      params = params.append('fax', partner.getFax());

      return this.http.put(`${environment.endpoints.app}/partner/${this.coreService.getNyomdaId()}`, params)
        .pipe(map((r) => this.entityService.parse(r).getResponse()));
    }

    /**
     * Név entitások lekérdezése a kiszolgálótól
     * 
     * @param filter 
     * @param isOrder 
     * @returns 
     */
    getNames(filter: string = '', isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-nevek/' : '';

      return this.http.get(`${environment.endpoints.app}/nev/${this.coreService.getNyomdaId()}/${path}${filter ?? ''}`)
        .pipe(map((r) => this.entityService.parse(r).getResponse()));
    }

    /**
     * Megváltoztatunk a backenden keresztül egy nevet az aktuális partnerhez, a path változó 
     * értéke alapértelmezésben üres, amennyiben a nevet a megrendelésből hozzuk létre, akkor 
     * a változónak meg kell adnunk az "megrendeles-nevek/" útvonalat (isOrder).
     * 
     * @param nev Név entitás
     * @param partner Partner kód
     * @param isOrder boolean
     * @returns 
     */
    createName(nev: Nev, partner: number, isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-nevek' : '';
      let params = new HttpParams();

      // megcsináljuk a paramsokat az entitás alapján
      params = params.set('partner', partner.toString());
      params = params.set('nev', nev.getNev());
      params = params.set('beosztas', nev.getBeosztas() ?? '');
      params = params.set('tel', SanitizerService.serialize(nev.getTel() ?? ''));
      params = params.set('tel2', SanitizerService.serialize(nev.getTel2() ?? ''));
      params = params.set('mobil', SanitizerService.serialize(nev.getMobil() ?? ''));
      params = params.set('email', SanitizerService.serialize(nev.getEmail() ?? ''));

      // a jogokat is tárolnunk kell
      Object.keys(nev).forEach(key => {
        if (key.startsWith('pp_jog')) {
          params = params.set(key, nev[key]);
        }
      });

      return this.http.post(`${environment.endpoints.app}/nev/${this.coreService.getNyomdaId()}/${path}`, params)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Megváltoztatunk a backenden keresztül egy nevet az aktuális partnerhez, a path változó 
     * értéke alapértelmezésben üres, amennyiben a nevet a megrendelésből hozzuk létre, akkor 
     * a változónak meg kell adnunk az "megrendeles-nevek/" útvonalat (isOrder).
     * 
     * @param nev Név entitás
     * @param partner Partner kód
     * @param isOrder boolean
     * @returns 
     */
    changeName(nev: Nev, partner: number, isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-nevek/' : '';
      let params = new HttpParams();

      // megcsináljuk a paramsokat az entitás alapján
      params = params.set('partner', partner.toString());
      params = params.set('nev', nev.getNev());
      params = params.set('beosztas', nev.getBeosztas());
      params = params.set('tel', SanitizerService.serialize(nev.getTel() ?? ''));
      params = params.set('tel2', SanitizerService.serialize(nev.getTel2() ?? ''));
      params = params.set('mobil', SanitizerService.serialize(nev.getMobil()));
      params = params.set('email', SanitizerService.serialize(nev.getEmail()));

      // a jogokat is tárolnunk kell
      Object.keys(nev).forEach(key => {
        if (key.startsWith('pp_jog')) {
          params = params.set(key, nev[key]);
        }
      });

      return this.http.put(`${environment.endpoints.app}/nev/${this.coreService.getNyomdaId()}/${path}${nev.getKod()}`, params)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Töröltre állítunk a backenden keresztül egy nevet az aktuális partnerhez, a path változó 
     * értéke alapértelmezésben üres (megrendelésből nem engedünk nevet törölni!)
     * 
     * @param nev Név entitás
     * @param partner Partner kód
     * @returns 
     */
    deleteName(nev: Nev, partner: number): Observable<CoreResponse> {      
      return this.http.delete(`${environment.endpoints.app}/nev/${this.coreService.getNyomdaId()}/${partner}/${nev.getKod()}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy adott partner összes címét kérdezzük le a kiszolgálótól, ha az isOrder true, akkor a MEGRENDELÉS-hez 
     * húzzuk be a partner címeket. 
     * 
     * @param filters 
     * @param isOrder 
     * @returns 
     */
    getPartnerCimek(filters: string, isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-partcimek/' : '';

      return this.http.get(`${environment.endpoints.app}/partcimek/${this.coreService.getNyomdaId()}/${path}${filters}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy partner címwet rögzítünk a rendszerben. Csak szállítási cím rögzítésére van lehetőség!
     * 
     * @param cim 
     * @param partner 
     * @param isOrder 
     * @returns 
     */
    createPartnerCim(cim: PartnerCim, partner: number, isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-partcimek' : '';
      let params = new HttpParams();

      // megcsináljuk a paramsokat az entitás alapján
      params = params.set('partner', partner.toString());
      params = params.set('ceg', SanitizerService.serialize(cim.ceg ?? ''));
      params = params.set('varos', SanitizerService.serialize(cim.varos ?? ''));
      params = params.set('utca', SanitizerService.serialize(cim.utca ?? ''));
      params = params.set('isz', SanitizerService.serialize(cim.isz ?? ''));
      params = params.set('adoszam', SanitizerService.serialize(cim.adoszam ?? ''));
      params = params.set('kontakt', SanitizerService.serialize(cim.kontakt ?? ''));

      return this.http.post(`${environment.endpoints.app}/partcimek/${this.coreService.getNyomdaId()}/${path}`, params)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy partner címwet rögzítünk a rendszerben. Csak szállítási cím rögzítésére van lehetőség!
     * 
     * @param cim 
     * @param partner 
     * @param isOrder 
     * @returns 
     */
    changePartnerCim(cim: PartnerCim, partner: number, isOrder: boolean = false): Observable<CoreResponse> {

      let path = (isOrder) ? 'megrendeles-partcimek/' : '';
      let params = new HttpParams();

      // megcsináljuk a paramsokat az entitás alapján
      params = params.set('partner', partner.toString());
      params = params.set('ceg', SanitizerService.serialize(cim.ceg ?? ''));
      params = params.set('varos', SanitizerService.serialize(cim.varos ?? ''));
      params = params.set('utca', SanitizerService.serialize(cim.utca ?? ''));
      params = params.set('isz', SanitizerService.serialize(cim.isz ?? ''));
      params = params.set('adoszam', SanitizerService.serialize(cim.adoszam ?? ''));
      params = params.set('kontakt', SanitizerService.serialize(cim.kontakt ?? ''));

      return this.http.put(`${environment.endpoints.app}/partcimek/${this.coreService.getNyomdaId()}/${path}${cim.getKod()}`, params)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Törlésre állítunk egy címet az adatbázisban
     * 
     * @param kod 
     * @returns 
     */
    deletePartnerCim(kod: number): Observable<CoreResponse> {
      return this.http.delete(`${environment.endpoints.app}/partcimek/${this.coreService.getNyomdaId()}/${kod}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Rögzítünk egy megrendelést a backend oldalon
     * 
     * @param params 
     * @returns 
     */
    submitRendeles(params: HttpParams): Observable<CoreResponse> {
      return this.http.post(`${environment.endpoints.app}/pp-rendeles-v3/${this.coreService.getNyomdaId()}`, params)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy adott táskaszám alapján betöltünk egy táskát
     * 
     * @param taskaszam 
     * @returns
     */
    getTaska(taskaszam: number): Observable<CoreResponse> {

      const filter = new CoreRequestParams([new CoreRequestFilterModel('szam', taskaszam)]);

      return this.http.get(`${environment.endpoints.app}/taska-v2/${this.coreService.getNyomdaId()}${filter.getQuery()}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy adott táskaszám alapján betöltünk egy táska részletek entitást
     * 
     * @param taskaszam 
     * @returns
     */
    getTaskaDetails(taskaszam: number): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/taska-details/${this.coreService.getNyomdaId()}/${taskaszam}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy adott táskaszám és hash alapján betöltünk egy táska fájlt
     *  
     * @param taskaszam 
     * @returns
     */
    getTaskaFile(taskaszam: number, hash: string, filters: string = ''): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/taska-files/${this.coreService.getNyomdaId()}/by-hash/${taskaszam}/${hash}${filters}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy adott táskaszám alapján betöltünk egy táska fájl listát
     *  
     * @param taskaszam 
     * @returns
     */
    getTaskaFiles(taskaszam: number, filters: string = ''): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/taska-files/${this.coreService.getNyomdaId()}/by-taska/${taskaszam}${filters}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Egy oldalkepek tömbben fellelhető hash értéket várunk bemenetként, ésszerűen az első oldal hash értékét. 
     * Visszakapjuk a teljes preflight hit, rule és page listát.
     * 
     * @param hash 
     * @returns 
     */
    getPreflightHitlist(hash: string): Observable<any> {
      return this.http.get(`${environment.endpoints.app}/preflight/${this.coreService.getNyomdaId()}/${hash}`); // .pipe(map(baseResult => new EntityService().map(baseResult).get()));
    }

    /**
     * Az aktuális szótár XML-t kapjuk vissza JSON formában
     * 
     * @returns 
     */
    getPreflightDictionary(): Observable<any> {
      return this.http.get(`${environment.endpoints.app}/preflight/${this.coreService.getNyomdaId()}/dictionary`);
    }

    /**
     * Preflight státuszolás batchban. Mivel más féle típus nincs is, így a single elemet is így adjuk át.
     * 
     * @param kod 
     * @param page_id 
     * @param statuses 
     */
    setPreflightStatus(kod: number, page_id: string, statuses: { rule_id: string, status: number }[]): Observable<any> {
    
      let params = new HttpParams();
      params = params.append('kod', kod);
      params = params.append('page_id', page_id);

      statuses.forEach(s => {
          params = params.append('statuses[]', page_id + ',' +  s.rule_id + ',' +  s.status);
      });
      
      return this.http.post(`${environment.endpoints.app}/preflight/${this.coreService.getNyomdaId()}/status`, params);
    }

    /**
     * Preflight státuszolás batchban. Mivel más féle típus nincs is, így a single elemet is így adjuk át.
     * 
     * @param kod 
     * @param page_id 
     * @param statuses 
     */
    setPreflightStatusMass(kod: number, statuses: { page_id: string, rule_id: string, status: number }[]): Observable<any> {
    
      let params = new HttpParams();
      params = params.append('kod', kod);
      params = params.append('page_id', 'mass'); // nem számít, nem kezeljük, csak figyeljük

      statuses.forEach(s => {
          params = params.append('statuses[]', s.page_id + ',' +  s.rule_id + ',' +  s.status);
      });
      
      return this.http.post(`${environment.endpoints.app}/preflight/${this.coreService.getNyomdaId()}/status`, params);
    }

    /**
     * Ez adott levélkód alapján betöltünk egy ajánlati levelet
     * 
     * @param levelkod 
     * @returns 
     */
    getLevel(levelkod: number): Observable<CoreResponse> {

      const params = new CoreRequestParams([
        new CoreRequestFilterModel('kod', levelkod) 
      ]);
  
      return this.http.get(`${environment.endpoints.app}/level-v2/${this.coreService.getNyomdaId()}${params.getQuery() ?? ''}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Ez adott levélkód alapján betöltünk egy ajánlati levelet. Az observer core feldolgozás 
     * nélkül küldi vissza az adatokat
     * 
     * @param kod 
     * @returns 
     */
    getLevelPure(levelkod: number): Observable<Object> {
      
      const params = new CoreRequestParams([
        new CoreRequestFilterModel('kod', levelkod) 
      ]);
  
      return this.http.get(`${environment.endpoints.app}/level-v2/${this.coreService.getNyomdaId()}${params.getQuery() ?? ''}`);
    }

    /**
     * Egy adott levélkód alapján lekérdezzük a kérdéseket
     * 
     * @param levelkod 
     * @returns 
     */
    getLevelKerdes(levelkod: number): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/levelkerdes-v2/${this.coreService.getNyomdaId()}/${levelkod}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Termékdíj kategóriákat kérdezzük le a kiszolgálóról
     * 
     * @returns 
     */
    getTermekdij(): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/termekdij/${this.coreService.getNyomdaId()}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Softproof adatokat kérünk le azonosító alapján a kiszolgálótól
     * 
     * @param id 
     * @returns 
     */
    getSoftproof(id: number): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/softproof/${this.coreService.getNyomdaId()}/${id.toString()}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }  

    /**
     * Szerszam adatokat kérünk le azonosító alapján a kiszolgálóról
     * 
     * @param id 
     * @returns 
     */
    getSzerszam(id: number): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/szerszam/${this.coreService.getNyomdaId()}/${id.toString()}`)
        .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * @param nyomda 
     * @returns 
     */
    getJobStats(): Observable<CoreResponse> {
      return this.http.get(`${environment.endpoints.app}/stat/${this.coreService.getNyomdaId()}/taska`)
      .pipe(map(r => this.entityService.parse(r).getResponse()));
    }

    /**
     * Valamilyen táska állományt töltünk fel a paramsban érkező formdata értékekenek megfelelően
     * 
     * @example
     * formData.append("taskaFile", event['target'].files[0]);
     * formData.append("nyomda_id", this.coreService.getNyomdaId().toString());
     * formData.append("taska_id", this.taska.szam.toString());
     * formData.append("target", "tdnyilatkozat"); // a target paraméter szabályozza, hogy kezeljük az állományt a backenden
     * 
     * 
     * @param params 
     * @returns 
     */
    uploadTaskaFile(params: FormData): Observable<any> {
      return this.http.post(`${environment.endpoints.app}/taska-files/${this.coreService.getNyomdaId()}/upload`, params, {
        reportProgress: true,
        observe: 'events'
      });
    }

    /**
     * Valamilyen típusú állományt fogunk letölteni a kiszolgálóról. Az identifier minden esetben a végpontra 
     * jellemző, tehát pl. fileservice esetén a hash lesz.
     * 
     * @param type 
     * @param identifier 
     * @returns 
     */
    downloadFile(type: TDownloadableFile, identifier: string): Observable<Blob> {

      switch (type) {
        case EDownloadableFile.FILESERVICE:
          return this.http.get(`${environment.endpoints.fileservice}/guest/download/${identifier}`, { responseType: 'blob' });
        case EDownloadableFile.SZAMLA:
          return this.http.get(`${environment.endpoints.app}/szamla/${this.coreService.getNyomdaId()}/${identifier}`, { responseType: 'blob' });
      }

    }

    /**
     * A download file által visszaadott response-re és a filenévre dolgozunk rá központosítva, mert nem akarunk 5x lekódolni valamit. 
     * 
     * @param response 
     * @param file 
     */
    downloadHandler(response, file: unknown, name: string = undefined): void {

      const binaryData = [];
      binaryData.push(response);
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
      downloadLink.setAttribute('download', name ?? file['nev']);
      document.body.appendChild(downloadLink);
      downloadLink.click();

    }
 
}